import type {
  ProjectReportPageEntity,
  ProjectReportPageFilters,
  ProjectReportRowEntity,
  ReportFormxAnswer,
  ReportFormxFieldMeta,
  ReportFormxItem,
  ReportFormxPhotoPreviewItem,
  ReportKpiItem,
  ReportSessionLocation,
} from "@/features/project/domain/entities/ProjectReportRowEntity.js";
import type { IProjectReportRepository } from "@/features/project/domain/repositories/IProjectReportRepository.js";
import { ProjectReportRemoteDataSource } from "@/features/project/data/datasources/ProjectReportRemoteDataSource.js";
import type {
  ProjectReportPageDto,
  ReportFormxSubmissionRowDto,
  ReportFormxValueRowDto,
  ReportSessionRowDto,
} from "@/features/project/data/dtos/ProjectReportDto.js";

/** A formx_value is stored as text; a photo field holds a JSON array of URLs (or a bare URL). */
function parseUrls(raw: string | null | undefined): string[] {
  if (raw == null || raw === '') return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String) : [String(parsed)];
  } catch {
    return [raw];
  }
}

/** Flatten every `photo`-type field's value(s) into one URL list for a submission. */
function collectFormxPhotos(values: ReportFormxValueRowDto[] | null | undefined): string[] {
  if (!values) return [];
  const urls: string[] = [];
  for (const v of values) {
    if (v.field?.field_type === 'photo') urls.push(...parseUrls(v.value));
  }
  return urls;
}

/** Non-media field types whose answer is worth showing as a "label: value" line. */
const ANSWER_FIELD_TYPES = new Set(['text', 'number', 'kpi', 'percentage', 'boolean']);

/** A formx_value is stored as `JSON.stringify([String(v)])` — unwrap back to the scalar. */
function parseScalarValue(raw: string): string {
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? String(parsed[0] ?? '') : String(parsed);
  } catch {
    return raw;
  }
}

function formatAnswerValue(fieldType: string, raw: string): string {
  const value = parseScalarValue(raw);
  if (fieldType === 'boolean') return value === 'true' ? 'Có' : 'Không';
  if (fieldType === 'percentage') return `${value}%`;
  return value;
}

/**
 * Collect every text/number/kpi/percentage/boolean answer of a submission, skipping
 * empty and media fields, ordered by the form's own field `sort_order` (same
 * order the field appeared when the submitter filled it in).
 */
function collectFormxAnswers(values: ReportFormxValueRowDto[] | null | undefined): ReportFormxAnswer[] {
  if (!values) return [];
  const answers: Array<ReportFormxAnswer & { sortOrder: number }> = [];
  for (const v of values) {
    const fieldType = v.field?.field_type;
    if (!fieldType || !ANSWER_FIELD_TYPES.has(fieldType)) continue;
    if (v.value == null || v.value === '') continue;
    answers.push({
      label: v.field?.label ?? 'Trường',
      fieldType,
      value: formatAnswerValue(fieldType, v.value),
      sortOrder: v.field?.sort_order ?? 0,
    });
  }
  return answers.sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * Labels of `photo`-type fields that have at least one photo, in field
 * `sort_order` — export-only, so the Report drawer's answer list (which reads
 * `answers`, not this) never shows a "PICTURE" placeholder line.
 */
function collectFormxPhotoFieldLabels(values: ReportFormxValueRowDto[] | null | undefined): string[] {
  if (!values) return [];
  const entries: Array<{ label: string; sortOrder: number }> = [];
  for (const v of values) {
    if (v.field?.field_type !== 'photo') continue;
    if (v.value == null || v.value === '') continue;
    entries.push({ label: v.field?.label ?? 'Trường', sortOrder: v.field?.sort_order ?? 0 });
  }
  return entries.sort((a, b) => a.sortOrder - b.sortOrder).map((e) => e.label);
}

/**
 * Every text/number/percentage/boolean field this submission has, filled or
 * left blank, in field `sort_order` — unlike `collectFormxAnswers`, does not
 * drop empty values. Export field-discovery reads this instead of `answers`
 * so a form isn't dropped from the sheet just because one of its fields was
 * left blank in every submission that used it.
 */
function collectFormxAllFields(
  values: ReportFormxValueRowDto[] | null | undefined,
): Array<ReportFormxFieldMeta & { sortOrder: number }> {
  if (!values) return [];
  const entries: Array<ReportFormxFieldMeta & { sortOrder: number }> = [];
  for (const v of values) {
    const fieldType = v.field?.field_type;
    if (!fieldType || !ANSWER_FIELD_TYPES.has(fieldType)) continue;
    entries.push({ label: v.field?.label ?? 'Trường', fieldType, sortOrder: v.field?.sort_order ?? 0 });
  }
  return entries.sort((a, b) => a.sortOrder - b.sortOrder);
}

function groupBySession<T>(rows: Array<{ session_id: string }>, map: (row: any) => T): Map<string, T[]> {
  const grouped = new Map<string, T[]>();
  for (const row of rows) {
    const list = grouped.get(row.session_id);
    if (list) list.push(map(row));
    else grouped.set(row.session_id, [map(row)]);
  }
  return grouped;
}

/** Full formx_submissions row (with values/fields embed) → the drawer/export's ReportFormxItem shape. */
function mapFormxRow(r: ReportFormxSubmissionRowDto): ReportFormxItem {
  return {
    submissionId: r.id,
    formId: r.form_id ?? null,
    formName: r.form?.name ?? null,
    formSortOrder: r.form?.sort_order ?? 0,
    submittedAt: r.submitted_at ?? null,
    photoUrls: collectFormxPhotos(r.values),
    answers: collectFormxAnswers(r.values),
    photoFieldLabels: collectFormxPhotoFieldLabels(r.values),
    allFields: collectFormxAllFields(r.values),
  };
}

/** get_project_formx_photo_preview's per-session rows (one per photo value, already rank-capped) → the table's thumbnail preview shape, merging same-submission values together. */
function mapPhotoPreviewRows(
  rows: { submissionId: string; submittedAt: string | null; value: string }[],
): ReportFormxPhotoPreviewItem[] {
  const bySubmission = new Map<string, ReportFormxPhotoPreviewItem>();
  for (const r of rows) {
    const urls = parseUrls(r.value);
    const existing = bySubmission.get(r.submissionId);
    if (existing) existing.photoUrls.push(...urls);
    else bySubmission.set(r.submissionId, { submissionId: r.submissionId, submittedAt: r.submittedAt, photoUrls: urls });
  }
  return Array.from(bySubmission.values());
}

function achieverName(profile: ReportSessionRowDto["achiever_profile"]): string | null {
  if (!profile) return null;
  const full = `${profile.first_name ?? ""} ${profile.last_name ?? ""}`.trim();
  return full || profile.email || null;
}

function mapLocation(location: ReportSessionRowDto["location"]): ReportSessionLocation | null {
  if (!location) return null;
  return {
    id: location.id,
    name: location.name ?? null,
    province: location.province?.full_name ?? location.province?.name ?? location.province_code ?? null,
    ward: location.ward?.full_name ?? location.ward?.name ?? location.ward_code ?? null,
    addressLine: location.address_line ?? null,
    address: location.full_address_cache ?? null,
    code: location.code ?? null,
    categories: location.categories ?? [],
  };
}

export class ProjectReportRepository implements IProjectReportRepository {
  constructor(private readonly remoteDataSource: ProjectReportRemoteDataSource) {}

  async getProjectReportPage(
    orgId: string,
    projectId: string,
    filters: ProjectReportPageFilters,
  ): Promise<ProjectReportPageEntity> {
    const page = await this.remoteDataSource.getProjectReportPage(orgId, projectId, filters);
    return {
      rows: (page.rows ?? []).map((row) => {
        const r = row as unknown as ProjectReportRowEntity;
        return {
          ...r,
          kpiItems: r.kpiItems ?? [],
          formxItems: r.formxItems ?? [],
          formxSubmissionCounts: (r.formxSubmissionCounts as Record<string, number>) ?? {},
          // The RPC deliberately returns at most five raw formx_values. Photo
          // values are JSON-encoded URL arrays, so preserve the legacy mapper's
          // URL normalization before the table renders thumbnail URLs.
          formxPhotoPreview: (r.formxPhotoPreview ?? []).map((preview) => ({
            ...preview,
            photoUrls: (preview.photoUrls ?? []).flatMap((value) => parseUrls(value)),
          })),
          checkinPhotoUrls: r.checkinPhotoUrls ?? [],
          checkoutPhotoUrls: r.checkoutPhotoUrls ?? [],
        };
      }),
      total: page.total ?? 0,
      summary: {
        completed: page.summary?.completed ?? 0,
        active: page.summary?.active ?? 0,
        scheduled: page.summary?.scheduled ?? 0,
        formxTotal: page.summary?.formxTotal ?? 0,
      },
      facets: {
        achievers: (page.facets?.achievers ?? [])
          .filter((item): item is { id: string; label: string } => !!item.id && !!item.label),
        locations: (page.facets?.locations ?? [])
          .filter((item): item is { id: string; label: string } => !!item.id && !!item.label),
      },
    };
  }

  async getProjectReport(
    orgId: string,
    projectId: string,
    includePhotoPreview = true,
  ): Promise<ProjectReportRowEntity[]> {
    const [sessions, kpiValues, formxCounts, photoPreviewBySession] = await Promise.all([
      this.remoteDataSource.listSessions(orgId, projectId),
      this.remoteDataSource.listKpiValues(orgId, projectId),
      this.remoteDataSource.listFormxSubmissionCounts(orgId, projectId),
      includePhotoPreview
        ? this.remoteDataSource.listFormxPhotoPreviewBySession(orgId, projectId)
        : Promise.resolve({}),
    ]);

    const kpiBySession = groupBySession<ReportKpiItem>(kpiValues, (r) => ({
      id: r.id,
      submissionId: r.submission_id,
      kpiId: r.kpi_id,
      kpiName: r.kpi?.name ?? null,
      unit: r.kpi?.unit ?? null,
      value: r.manual_value ?? r.system_value ?? 0,
      createdAt: r.created_at ?? null,
    }));

    const formxCountBySession = new Map<string, number>();
    const formxSubmissionCountsBySession = new Map<string, Record<string, number>>();
    for (const r of formxCounts) {
      formxCountBySession.set(r.session_id, (formxCountBySession.get(r.session_id) ?? 0) + 1);
      if (r.form_id) {
        const counts = formxSubmissionCountsBySession.get(r.session_id) ?? {};
        counts[r.form_id] = (counts[r.form_id] ?? 0) + 1;
        formxSubmissionCountsBySession.set(r.session_id, counts);
      }
    }
    const formxPhotoPreviewBySession = new Map<string, ReportFormxPhotoPreviewItem[]>();
    for (const [sessionId, rows] of Object.entries(photoPreviewBySession)) {
      formxPhotoPreviewBySession.set(sessionId, mapPhotoPreviewRows(rows));
    }

    return sessions.map((s) => {
      const kpiItems = kpiBySession.get(s.id) ?? [];
      return {
        sessionId: s.id,
        achieverId: s.achiever_id ?? null,
        achieverName: achieverName(s.achiever_profile),
        achieverEmail: s.achiever_profile?.email ?? null,
        locationId: s.location_id ?? null,
        location: mapLocation(s.location),
        kpiCount: kpiItems.length,
        formxCount: formxCountBySession.get(s.id) ?? 0,
        formxSubmissionCounts: formxSubmissionCountsBySession.get(s.id) ?? {},
        kpiItems,
        formxItems: [],
        formxPhotoPreview: formxPhotoPreviewBySession.get(s.id) ?? [],
        checkinAt: s.started_at ?? null,
        checkoutAt: s.ended_at ?? null,
        scheduledStartAt: s.scheduled_start_at ?? null,
        scheduledEndAt: s.scheduled_end_at ?? null,
        checkinPhotoUrls: s.start_photo_urls ?? [],
        checkoutPhotoUrls: s.end_photo_urls ?? [],
      };
    });
  }

  /** Full formx detail for one session, fetched lazily when its report-drawer row is opened. */
  async getSessionFormxDetail(orgId: string, sessionId: string): Promise<ReportFormxItem[]> {
    const rows = await this.remoteDataSource.listFormxSubmissionsForSession(orgId, sessionId);
    return rows.map(mapFormxRow);
  }

  /** Full formx detail for every session in the project, grouped by session_id — fetched lazily via RPC, only when the export modal opens. */
  async getProjectFormxDetailBySession(orgId: string, projectId: string): Promise<Map<string, ReportFormxItem[]>> {
    const bySession = await this.remoteDataSource.listFormxSubmissionsFullBySession(orgId, projectId);
    const result = new Map<string, ReportFormxItem[]>();
    for (const [sessionId, rows] of Object.entries(bySession)) {
      result.set(sessionId, rows.map(mapFormxRow));
    }
    return result;
  }
}
